import { useMemo } from 'react'
import { format } from 'date-fns'

import { TableCell } from '@mui/material'
import { warnDev } from '@/utils/warnDev'

import { COLUMN_CELL_TYPE } from '../TableBase.constants'
import type { Column, PathType } from '../TableBase.types'
import { getPropertyAtPath } from '../utils/getPropertyAtPath'

type Props<Data extends Record<string, unknown>, SortAttribute extends string> = {
  column: Column<Data, SortAttribute>
  row: Data
}

export const Cell = <Data extends Record<string, unknown>, SortAttribute extends string>({
  row,
  column
}: Props<Data, SortAttribute>) => {
  const dataAtPath = getPropertyAtPath(row, column.id as PathType<Data>)

  // Usage of any is intentional here, as the type of the dataAtPath is unknown at this point
  // And typescript has its limitations for narrowing that complex generic type
  // But if anyone has a minute to spare, feel free to improve this
  // As this is generic component and we know what we are doing here, we can safely ignore this
  // for the sake of good DX when using the column definition in the table
  const columnContent = useMemo(() => {
    switch (column.type) {
      case COLUMN_CELL_TYPE.TEXT: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = (column.transform?.(dataAtPath as any) ?? dataAtPath) || column?.noDataFallback

        if (typeof data !== 'string' && typeof data !== 'number') {
          warnDev(
            `Cell: Column with type text should return a string, but received: ${typeof data}`
          )
          return <>{JSON.stringify(data)}</>
        }

        return <>{data}</>
      }

      case COLUMN_CELL_TYPE.NUMBER: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data = column.transform?.(dataAtPath as any) ?? dataAtPath

        if (
          typeof data !== 'number' &&
          !column?.noDataFallback &&
          typeof data !== 'string' &&
          data !== null
        ) {
          warnDev(
            `Cell: Column with type number should return a number, but received: ${typeof data}`
          )
          return <>{JSON.stringify(data)}</>
        } else if (typeof data === 'number' && column.precision !== undefined && !isNaN(data)) {
          data = Number(data.toFixed(column.precision))
        }

        return <>{data ?? column?.noDataFallback}</>
      }

      case COLUMN_CELL_TYPE.DATE: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data = column.transform?.(dataAtPath as any) ?? dataAtPath

        if (
          typeof data !== 'string' &&
          typeof data !== 'number' &&
          !(data instanceof Date) &&
          column.dateFormat &&
          !column?.noDataFallback
        ) {
          warnDev(
            `Cell: Column with type date should return a string, number or Date, but received: ${typeof data}`
          )
          return <>{column?.noDataFallback || null}</>
        } else if (
          (typeof data === 'string' || typeof data === 'number' || data instanceof Date) &&
          column.dateFormat
        ) {
          data = format(new Date(data), column.dateFormat)
        }

        return <>{data ?? column?.noDataFallback}</>
      }

      case COLUMN_CELL_TYPE.COMPONENT: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return <>{column.render(dataAtPath as any, row)}</>
      }

      default: {
        const errorColumn: never = column
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        warnDev(`Cell: Unhandled column type: ${(errorColumn as any)?.type}`)

        return null
      }
    }
  }, [column, dataAtPath])

  return (
    <TableCell
      align={column?.align}
      padding={column.type === COLUMN_CELL_TYPE.COMPONENT ? 'none' : 'normal'}
      sx={
        column?.sx && typeof column.sx === 'function'
          ? (theme) => ({
              px: column.type === COLUMN_CELL_TYPE.COMPONENT ? 1 : undefined,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...(column.sx as any)(theme)
            })
          : {
              px: column.type === COLUMN_CELL_TYPE.COMPONENT ? 1 : undefined,
              ...column?.sx
            }
      }
    >
      {columnContent}
    </TableCell>
  )
}
